import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Depth, TopicPlanOutput } from "@/api/create-types";
import { Alert, Button } from "@/components/ui";
import type { RendererProps } from "./types";

const DEPTHS: { value: Depth; label: string }[] = [
  { value: "already_know", label: "I know this" },
  { value: "recap_briefly", label: "Recap briefly" },
  { value: "dig_deeper", label: "Dig deeper" },
  { value: "from_scratch", label: "From scratch" },
];

interface TopicState {
  included: boolean;
  depth: Depth;
  countOverride: string;
  dropped: string[]; // proposed mode_ids the user removed (language-trainer)
  added: string[]; // extra mode_ids the user added (language-trainer)
}

/** Renderer for the topic-plan family (headteacher.topic-plan / math-topic-plan /
 *  de-grammar-topic-plan). The user picks which proposed topics to study and a
 *  depth per topic. Mirrors iOS HeadteacherReviewView. */
export function TopicPlanReview({ step, submitting, error, onAdvance }: RendererProps) {
  const { t } = useTranslation();
  const output = (step.output ?? { topics: [] }) as unknown as TopicPlanOutput;
  const topics = output.topics ?? [];
  // Language-trainer only: per-language drill catalog → the "add drill" picker.
  const availableModes = output.available_modes ?? [];
  const modesEnabled = availableModes.length > 0;
  const modeLabel = (id: string) =>
    availableModes.find((m) => m.mode_id === id)?.label ?? id;

  const [state, setState] = useState<Record<string, TopicState>>(() =>
    Object.fromEntries(
      topics.map((t) => [
        t.id,
        {
          included: true,
          depth: t.depth_suggestion,
          countOverride: "",
          dropped: [],
          added: [],
        } as TopicState,
      ]),
    ),
  );

  const update = (id: string, patch: Partial<TopicState>) =>
    setState((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const selectedCount = topics.filter((t) => state[t.id]?.included).length;

  // Active drills for a topic = proposed (minus dropped) + added.
  const proposedIds = (tp: (typeof topics)[number]) =>
    (tp.proposed_modes ?? []).map((m) => m.mode_id);
  const activeModes = (tp: (typeof topics)[number]) => {
    const st = state[tp.id];
    if (!st) return [] as string[];
    return [...proposedIds(tp).filter((id) => !st.dropped.includes(id)), ...st.added];
  };
  const removeMode = (tp: (typeof topics)[number], modeId: string) => {
    const st = state[tp.id];
    if (proposedIds(tp).includes(modeId)) {
      update(tp.id, { dropped: [...st.dropped, modeId] });
    } else {
      update(tp.id, { added: st.added.filter((m) => m !== modeId) });
    }
  };
  const addMode = (tp: (typeof topics)[number], modeId: string) => {
    const st = state[tp.id];
    // Re-adding a previously dropped proposed mode just un-drops it.
    if (proposedIds(tp).includes(modeId)) {
      update(tp.id, { dropped: st.dropped.filter((m) => m !== modeId) });
    } else if (!st.added.includes(modeId)) {
      update(tp.id, { added: [...st.added, modeId] });
    }
  };

  const submit = () => {
    const included = topics.filter((t) => state[t.id]?.included);
    const edits = included.flatMap((tp) =>
      state[tp.id].dropped
        .filter((id) => proposedIds(tp).includes(id))
        .map((mode_id) => ({ topic_id: tp.id, mode_id, action: "drop" as const })),
    );
    const additions = included.flatMap((tp) =>
      state[tp.id].added.map((mode_id) => ({
        topic_id: tp.id,
        mode_id,
        intensity: "medium",
      })),
    );
    const payload = {
      topics: included.map((t) => {
        const st = state[t.id];
        const override = st.countOverride.trim() ? Number(st.countOverride) : undefined;
        return {
          topic_id: t.id,
          depth: st.depth,
          ...(override && !Number.isNaN(override) ? { question_count_override: override } : {}),
        };
      }),
      ...(edits.length ? { proposed_mode_edits: edits } : {}),
      ...(additions.length ? { proposed_mode_additions: additions } : {}),
    };
    onAdvance(payload);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold tracking-tight">{t("Choose what to study")}</h2>
        <p className="mt-1 text-sm text-ink-faint">
          {t("Eddi proposed {{count}} topics. Pick the ones you want and how deep to go.", { count: topics.length })}
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="space-y-3">
        {topics.map((tp) => {
          const st = state[tp.id];
          if (!st) return null;
          return (
            <div
              key={tp.id}
              className={`card p-4 transition ${st.included ? "" : "opacity-60"}`}
            >
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={st.included}
                  onChange={(e) => update(tp.id, { included: e.target.checked })}
                  className="mt-1 h-5 w-5 rounded border-black/20 text-brand-600 focus:ring-brand-400"
                />
                <span className="flex-1">
                  <span className="block font-semibold text-ink">{tp.label}</span>
                  {tp.summary && <span className="mt-0.5 block text-sm text-ink-faint">{tp.summary}</span>}
                </span>
              </label>

              {st.included && (
                <div className="mt-3 flex flex-wrap items-center gap-2 pl-8">
                  <div className="flex flex-wrap gap-1.5">
                    {DEPTHS.map((d) => (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => update(tp.id, { depth: d.value })}
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                          st.depth === d.value
                            ? "bg-brand-600 text-white"
                            : "bg-brand-50 text-brand-700 hover:bg-brand-100"
                        }`}
                      >
                        {t(d.label)}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    min={1}
                    value={st.countOverride}
                    onChange={(e) => update(tp.id, { countOverride: e.target.value })}
                    placeholder={t("{{count}} Qs", { count: tp.question_count_suggestion })}
                    className="ml-auto w-24 rounded-lg border border-black/10 px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </div>
              )}

              {st.included && modesEnabled && (
                <div className="mt-3 pl-8">
                  <span className="block text-xs font-medium text-ink-faint">{t("Drills")}</span>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {activeModes(tp).map((modeId) => (
                      <span
                        key={modeId}
                        className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700"
                      >
                        {modeLabel(modeId)}
                        <button
                          type="button"
                          aria-label={t("Remove {{label}}", { label: modeLabel(modeId) })}
                          onClick={() => removeMode(tp, modeId)}
                          className="text-brand-400 hover:text-brand-700"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                    {availableModes.filter((m) => !activeModes(tp).includes(m.mode_id)).length > 0 && (
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) addMode(tp, e.target.value);
                        }}
                        className="rounded-full border border-dashed border-brand-300 bg-white px-2.5 py-1 text-xs font-semibold text-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-400"
                      >
                        <option value="">{t("+ Add drill")}</option>
                        {availableModes
                          .filter((m) => !activeModes(tp).includes(m.mode_id))
                          .map((m) => (
                            <option key={m.mode_id} value={m.mode_id}>
                              {m.label}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button onClick={submit} size="lg" loading={submitting} disabled={selectedCount === 0}>
          {t("Generate {{count}} topics", { count: selectedCount })}
        </Button>
        {selectedCount === 0 && (
          <span className="text-sm text-ink-faint">{t("Select at least one topic.")}</span>
        )}
      </div>
    </div>
  );
}
